import {toast} from '@/utilities/toast/toast'
import {useTranslation} from '@/translations/i18n'
import {useMemo, useRef} from 'react'
import {FormProvider, useForm} from 'react-hook-form'
import {useToggle} from '@/hooks/useToggle'
import {useNavigate, useOutletContext} from 'react-router-dom'
import {PatientExtended} from '@/features/patients/types'
import {zodResolver} from '@hookform/resolvers/zod'
import {formatDate} from '@/dayjs'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {Divider} from '@/components/ui/divider/Divider'
import {PageContent} from '@/components/ui/page-atoms/PageAtoms'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {BannerAlert} from '@/components/commons/banner-alert/BannerAlert'
import {UnsavedBlocker} from '@/components/commons/unsaved-blocker/UnsavedBlocker'
import {MedicalReportExtended} from '@/features/medical-reports/types'
import {
    getAttributesFromValues,
    remapMedicalReportAttributesToDefaultValues,
    remapMedicalReportAttributesToPayload
} from '@/features/medical-reports/utils'
import {MedicalReportValidationSchema} from '@/features/medical-reports/formSchemas'
import {useUpdateMedicalReport} from '@/features/medical-reports/queries/useUpdateMedicalReport'
import {useMedicalReportFormStatus} from '@/features/medical-reports/hooks/useMedicalReportFormStatus'
import {HttpMedicalReportPayload} from '@/features/medical-reports/http/medicalReports.http'
import {MedicalReportFormHeader} from '@/features/medical-reports/components/medical-report-form-header/MedicalReportFormHeader'
import {MedicalReportFields} from '@/features/medical-reports/components/medical-report-fields/MedicalReportFields'
import {MedicalReportConfirmationModal} from '@/features/medical-reports/components/medical-report-confirmation-modal/MedicalReportConfirmationModal'
import {ProjectPlannedAccess} from '@/features/planned-accesses/types'
import {isAxiosError} from 'axios'

export const EditMedicalReportForm = ({
    medicalReport,
    initialPlannedAccesses
}: {
    medicalReport: MedicalReportExtended
    initialPlannedAccesses?: ProjectPlannedAccess
}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()

    const {isActive: isConfirmationModalVisible, on: showConfirmationModal, off: dismissConfirmationModal} = useToggle()
    const {isActive: isErrorBannerVisible, on: showErrorBanner, off: dismissErrorBanner} = useToggle()

    const viewportRef = useRef<HTMLDivElement>(null)
    const errorBannerRef = useRef<HTMLDivElement>(null)

    const {
        patient: {id: patientId, fullName, medicalRecord}
    } = useOutletContext<{patient: PatientExtended}>()

    const attributes = useMemo(
        () =>
            remapMedicalReportAttributesToDefaultValues(
                medicalReport.medicalRecordReportAttributes,
                initialPlannedAccesses
            ),
        [medicalReport.medicalRecordReportAttributes, initialPlannedAccesses]
    )

    const form = useForm<MedicalReportValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(MedicalReportValidationSchema),
        defaultValues: {
            medicalRecordId: medicalRecord?.id,
            endDate: medicalReport.project?.endDate ? new Date(medicalReport.project.endDate) : null,
            reportDate: medicalReport.date ? new Date(medicalReport.date) : undefined,
            reportType: medicalReport.medicalRecordReportType,
            reportTypeKeyName: medicalReport.medicalRecordReportType.keyName,
            project: medicalReport?.project,
            status: medicalReport.status,
            signedForUser: medicalReport.signedForUserId
                ? {id: medicalReport.signedForUserId, fullName: medicalReport.signedForFullName ?? ''}
                : undefined,
            isReportAccessDetatched:
                medicalReport.status !== 'draft' && (!medicalReport.accesses || medicalReport.accesses?.length == 0),
            accesses: medicalReport.accesses || undefined,
            noProject: medicalReport.medicalRecordReportType.keyName == 'document' && !medicalReport.project,
            ...attributes
        }
    })

    const updateMedicalReportMutation = useUpdateMedicalReport(medicalReport.id, {
        onSuccess: async () => {
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENT_MEDICAL_RECORD]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.MEDICAL_REPORTS]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT, medicalReport.project?.id]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS_BY_PATIENT]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS_BY_PATIENT, patientId]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENT_MEDICAL_RECORD]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.MEDICAL_REPORTS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USERS_BY_PATIENT]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.MEDICAL_REPORT_TYPES_BY_PATIENT]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.MEDICAL_REPORT_DATES_BY_PATIENT]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, medicalReport.project?.id]})
            toast.success(t('medical_records:medical_report_update_success'))
            navigate(-1)
        },
        onError: errors => {
            dismissConfirmationModal()
            if (isAxiosError(errors) && errors.response?.data?.code == 'medical-record-report-cannot-edit') {
                showErrorBanner()
                if (viewportRef.current) {
                    viewportRef.current.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    })
                }

                toast.error(t('medical_records:medical_report_cannot_edit'))
            }

            if (errors?.response?.data.errors) {
                showErrorBanner()
                if (viewportRef.current) {
                    viewportRef.current.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    })
                }
            }
        }
    })

    const formStatus = useMedicalReportFormStatus({
        form,
        mutation: updateMedicalReportMutation
    })

    // Function to handle the creation of a medical report
    const updateMedicalReport = (formValues: MedicalReportValidationSchema) => {
        // Extract and map the form values into the required payload structure
        const attributesFormValues = getAttributesFromValues(formValues)
        const attributes = remapMedicalReportAttributesToPayload(attributesFormValues)
        // Execute the mutation with the parsed payload
        updateMedicalReportMutation.mutate(
            HttpMedicalReportPayload.parse({
                medicalRecordReportTypeId: formValues.reportType.id,
                medicalRecordId: formValues.medicalRecordId,
                projectId: formValues.noProject ? null : formValues.project?.id,
                status: formValues.status,
                signedForUserId: formValues.signedForUser?.id ?? null,
                date: formatDate(formValues.reportDate.toISOString(), 'YYYY-MM-DD'),
                accessIds: formValues.accesses?.map(access => access.id),
                attributes
            })
        )
    }

    // Main function for handling form submission
    const handleSubmitForm = form.handleSubmit(
        // Success callback when the form validation passes
        () => {
            if (isErrorBannerVisible) {
                dismissErrorBanner()
            }
            showConfirmationModal()
        },
        // Error callback when the form validation fails
        errors => {
            console.error(errors)
            showErrorBanner()
            if (viewportRef.current) {
                viewportRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }
        }
    )

    // Function to handle the form submission logic based on the action (draft save or submit)
    const handleSubmitControl = ({isDraftSaving}: {isDraftSaving: boolean}) => {
        if (isDraftSaving) {
            // If saving as draft, directly get form values and create the report
            const formValues = form.getValues()
            updateMedicalReport(formValues)
        } else {
            // Otherwise, trigger the full form submission process
            handleSubmitForm()
        }
    }

    // Function to handle confirmation of form submission
    const handleConfirmSubmit = form.handleSubmit(updateMedicalReport)

    return (
        <FormProvider {...form}>
            <MedicalReportFormHeader
                isFormBlocked={formStatus.isDisabled}
                onSubmit={handleSubmitControl}
                isPendingSubmit={formStatus.isPendingSubmit}
                isPendingDraftSaving={formStatus.isPendingDraftSaving}
            />
            <Divider direction="horizontal" />
            <PageContent padding={0}>
                <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />} viewportProps={{ref: viewportRef}}>
                    {isErrorBannerVisible && (
                        <>
                            <BannerAlert ref={errorBannerRef} onClose={() => dismissErrorBanner()} />
                            <Divider direction="horizontal" />
                        </>
                    )}
                    <UnsavedBlocker isDirty={formStatus.isDirty}>
                        <MedicalReportFields
                            view="edit"
                            isTypeProjectCompliant={!formStatus.isDisabled}
                            status={medicalReport.status}
                        />
                        {isConfirmationModalVisible && (
                            <MedicalReportConfirmationModal
                                onConfirm={handleConfirmSubmit}
                                onClose={dismissConfirmationModal}
                                isLoading={updateMedicalReportMutation.isPending}
                                patientFullName={fullName}
                            />
                        )}
                    </UnsavedBlocker>
                </ScrollArea>
            </PageContent>
        </FormProvider>
    )
}
